// @owners { team: patients-team }
import { type AltoIconName } from '@alto/design-library-tokens';
import { InputAutocomplete, InputSelect, LgSpacing } from '@alto/design-system';
import { FertilityCycleTypeMap } from '@alto/scriptdash/alto/fertility_cycles/types/v1/fertility_cycle';
import React from 'react';
import { employersDataSource } from '~shared/data-sources/employersDataSource';
import formatPaymentMethod from '~shared/features/payment-methods/helpers/formatPaymentMethod';
import { type PaymentMethod } from '~shared/types';

type FormFieldProps = {
  readonly paymentMethods: PaymentMethod[];
  readonly selectedBackupPaymentMethodId?: number;
  readonly handleChangeEmployer: (employer: string) => void;
  readonly handleSelectCycleType: (cycleType: string) => void;
  readonly handleSelectPaymentMethod: (paymentMethodId: string) => void;
};

const HUMANIZED_FERTILITY_CYCLE_NAMES = {
  [FertilityCycleTypeMap.IUI]: 'IUI',
  [FertilityCycleTypeMap.RETRIEVAL]: 'Retrieval',
  [FertilityCycleTypeMap.TRANSFER]: 'Transfer',
  [FertilityCycleTypeMap.MALE_FERTILITY]: 'Male Fertility',
  [FertilityCycleTypeMap.PREGNANCY_MONITORING]: 'Pregnancy Monitoring',
};

export const OrderEarlyFormFields = ({
  paymentMethods,
  handleChangeEmployer,
  handleSelectCycleType,
  handleSelectPaymentMethod,
  selectedBackupPaymentMethodId,
}: FormFieldProps) => {
  return (
    <>
      <InputAutocomplete
        accessibilityLabel="Current Employer"
        label="Current Employer"
        leftIcon="bank-small"
        title="Find current employer"
        onSelectCallback={(data) => {
          const employer = data[0]?.value || '';
          handleChangeEmployer(employer);
        }}
        dataSource={employersDataSource}
        placeholder="Enter Employer"
        required
        onSelectAutoCloseActionSheet
        shouldAllowManualInput
      />
      <LgSpacing />
      <InputSelect
        placeholder="Select Cycle Type"
        label="Cycle Type"
        onValueChange={handleSelectCycleType}
        options={Object.values(FertilityCycleTypeMap).map((cycleType) => {
          return {
            label: HUMANIZED_FERTILITY_CYCLE_NAMES[cycleType],
            value: cycleType,
          };
        })}
      />
      <LgSpacing />
      <InputSelect
        placeholder="Select Backup Payment Method"
        label="Backup Payment Method"
        modalTitle="Select Backup Payment Method"
        caption="If Progyny denies your coverage, you will need to cover any costs out of pocket."
        description="If Progyny denies your coverage, you will need to cover any costs out of pocket."
        onValueChange={handleSelectPaymentMethod}
        defaultValue={selectedBackupPaymentMethodId?.toString()}
        options={[
          ...paymentMethods.map((pm: PaymentMethod) => {
            return {
              label: formatPaymentMethod(pm),
              value: pm.id.toString(),
              icon: (pm.type === 'PlaidData' ? 'bank' : 'creditcard') as AltoIconName,
            };
          }),
          {
            label: 'New Payment Method',
            value: 'create_new',
            icon: 'pluscircle',
          },
        ]}
      />
    </>
  );
};
